import { convertTrademark, getFormattedGameTime } from 'utils/game';
import {
  BoxscoreDetails,
  ChampionshipTeam,
  TeamLogoConfig,
  TeamsOnDeck,
  TeamStatsBasketball,
} from '../types/generated-types';
import { DEFAULT_COLOR, DEFAULT_TEAM_NAME } from '../defaults/default-data-types';
import { liveTeamStore } from './mml-apollo-cache';

export function createFormattedRoundTitle(
  roundTitle: string | null | undefined,
  regionTitle: string | null | undefined,
): string {
  return `${[convertTrademark(roundTitle).toLowerCase(), regionTitle].filter(Boolean).join(' - ')}`;
}

export function createFormattedClock(
  clock: string | null | undefined,
  period: string | number | null | undefined,
): string {
  return [clock, period].filter(Boolean).join(' - ');
}

export function createFormattedVenueTitle(
  city: string | null | undefined,
  state: string | null | undefined,
  venue: string | null | undefined,
): string {
  return `${[venue, [city, state].filter(Boolean).join(', ')].filter(Boolean).join(' - ')}`;
}

export function getTeamsOnDeck(teams: ChampionshipTeam[] | undefined): TeamsOnDeck {
  const length = teams ? teams.length : 0;
  switch (length) {
    case 1:
      return TeamsOnDeck.One;
    case 2:
      return TeamsOnDeck.Two;
  }
  return TeamsOnDeck.None;
}

export function createTeamLogo(team: ChampionshipTeam | BoxscoreDetails): TeamLogoConfig {
  return {
    color: (team.color as string) ?? DEFAULT_COLOR,
    logoUri: (team.seoname as string) ?? '',
    name: (team.nameShort as string) ?? DEFAULT_TEAM_NAME,
  };
}

export function addToLiveTeamStore(team: ChampionshipTeam | null) {
  const liveTeams = liveTeamStore();
  if (team && team.ncaaOrgId && !liveTeams[team.ncaaOrgId]) {
    liveTeams[team.ncaaOrgId] = team;
    liveTeamStore(liveTeams);
  }
}

export function createSeasonAverage(team: ChampionshipTeam | null): TeamStatsBasketball | null {
  if (team?.seasonAverage) return team.seasonAverage as TeamStatsBasketball;
  return null;
}

export function createStartTime(
  hasStartTime: boolean | null | undefined,
  convertTime: string | number | null | undefined,
): string {
  if (hasStartTime) {
    return typeof convertTime == 'number' ? getFormattedGameTime(convertTime) : '';
  } else {
    return 'TBA';
  }
}

function filterArrayByAttrValue(
  array: ChampionshipTeam[] | BoxscoreDetails[],
  attr: keyof ChampionshipTeam | keyof BoxscoreDetails,
  value: boolean,
  defaultValue: ChampionshipTeam | BoxscoreDetails | null,
): ChampionshipTeam | BoxscoreDetails | null {
  return (
    (array as ChampionshipTeam[] | BoxscoreDetails[]).find((item) => {
      if (array[0].__typename === 'ChampionshipTeam') {
        return (item as ChampionshipTeam)[attr as keyof ChampionshipTeam] === undefined
          ? false
          : (item as ChampionshipTeam)[attr as keyof ChampionshipTeam] === value;
      }
      if (array[0].__typename === 'BoxscoreDetails') {
        return (item as BoxscoreDetails)[attr as keyof BoxscoreDetails] === undefined
          ? false
          : (item as BoxscoreDetails)[attr as keyof BoxscoreDetails] === value;
      }
      return defaultValue;
    }) ?? defaultValue
  );
}
export function getHomeTeam(teams: ChampionshipTeam[], defaultValue: ChampionshipTeam | null): ChampionshipTeam | null {
  return filterArrayByAttrValue(teams, 'isHome', true, defaultValue) as ChampionshipTeam | null;
}
export function getVisitorTeam(
  teams: ChampionshipTeam[],
  defaultValue: ChampionshipTeam | null,
): ChampionshipTeam | null {
  return filterArrayByAttrValue(teams, 'isHome', false, defaultValue) as ChampionshipTeam | null;
}
export function getTopTeam(teams: ChampionshipTeam[], defaultValue: ChampionshipTeam | null): ChampionshipTeam | null {
  return filterArrayByAttrValue(teams, 'isTop', true, defaultValue) as ChampionshipTeam | null;
}
export function getBottomTeam(
  teams: ChampionshipTeam[],
  defaultValue: ChampionshipTeam | null,
): ChampionshipTeam | null {
  return filterArrayByAttrValue(teams, 'isTop', false, defaultValue) as ChampionshipTeam | null;
}

export function getHomeBoxscore(
  boxscores: BoxscoreDetails[],
  defaultValue: BoxscoreDetails | null,
): BoxscoreDetails | null {
  return filterArrayByAttrValue(boxscores, 'isHome', true, defaultValue) as BoxscoreDetails | null;
}
export function getVisitorBoxscore(
  boxscores: BoxscoreDetails[],
  defaultValue: BoxscoreDetails | null,
): BoxscoreDetails | null {
  return filterArrayByAttrValue(boxscores, 'isHome', false, defaultValue) as BoxscoreDetails | null;
}
